import { InfoCircleOutlined } from '@ant-design/icons';
import { Form, InputNumber, Radio, Table, Tooltip } from 'antd';
import { useContext, useEffect, useMemo } from 'react';

import { SCard } from 'app/components/StaycationUI';
import { computeExperienceName } from 'app/components/commons/Experience/utils';
import { FormLineLayout } from 'app/components/commons/Form/FormLineLayout/FormLineLayout';
import { useDayColumns } from 'app/components/pages/Experiences/Common/_components/Availability/_components/DailyPriceConfigurationTable/hooks/useDayColumns';
import { ViewEditModeContext } from 'app/context/ViewEditModeContext/ViewEditModeContext';
import { useGetExperiencesByHotelId } from 'app/hooks/data/core/admin/hotels/useHotelExperiences';
import { useGetHotel } from 'app/hooks/data/hotels/useHotel';
import { useExperiencesCategories } from 'app/hooks/data/useExperience';
import { DAY_OF_WEEK } from 'app/typings/core/admin/enum/dayOfWeek';
import { PAX_TYPE } from 'app/typings/core/admin/enum/paxType';
import type {
  MaxBookingConfiguration,
  PackResponse,
} from 'app/typings/core/admin/packs/pack';
import { pluralize } from 'app/utils/strings';

import './Limitations.scss';

export const Limitations = () => {
  const { isEditMode } = useContext(ViewEditModeContext);
  const form = Form.useFormInstance<
    Partial<PackResponse> & {
      maxAllowedAdult: number;
      maxAllowedChild: number;
      maxAllowedPax: number;
      minChargedPax: number;
      maxBookingConfiguration: MaxBookingConfiguration;
    }
  >();

  const baseColumns = useDayColumns(Object.values(DAY_OF_WEEK));
  const isDaytime = Form.useWatch('isDaytime', form);

  const dataSource = [{ key: 'max-booking-row' }];

  const columnsWithInputs = useMemo(() => {
    return baseColumns.map((col) => {
      const columnKey =
        'key' in col && col.key !== null && col.key !== undefined
          ? col.key
          : 'title' in col
          ? col.title
          : 'column';

      return {
        ...col,
        render: (_value: unknown, row: { key: string | number }) => (
          <Form.Item
            name={[
              'maxBookingConfiguration',
              'days',
              String(columnKey).toUpperCase(),
              'maxBooking',
            ]}
            rules={[
              {
                required: true,
                message: 'Required',
              },
            ]}
          >
            <InputNumber
              min={0}
              style={{ width: '100%' }}
              disabled={!isEditMode}
            />
          </Form.Item>
        ),
      };
    });
  }, [baseColumns, isEditMode]);

  const maxBookingConfiguration = Form.useWatch(
    'maxBookingConfiguration',
    form
  );
  const hotelId = Form.useWatch('hotelId', form);
  const minChargedPax = Form.useWatch('minChargedPax', form);
  const maxAllowedPax = Form.useWatch('maxAllowedPax', form);

  const { data: hotel } = useGetHotel(hotelId);
  const { data: hotelExperiences } = useGetExperiencesByHotelId(hotelId);
  const { data: experienceCategories } = useExperiencesCategories();

  const isDisabledBarBbHotel = hotel?.connectedBarBb && !isDaytime;

  useEffect(() => {
    if (isDisabledBarBbHotel) {
      form.setFieldsValue({ minChargedPax: 2, maxAllowedPax: 2 });
    }
  }, [isDisabledBarBbHotel, form]);

  const hasExperienceWithGuests = hotelExperiences?.experiences?.find(
    (experience) => experience.maxPax && experience.maxPax > 0
  );

  const hasExperienceWithoutChildren = hotelExperiences?.experiences?.some(
    (experience) =>
      experience.maxPax &&
      experience.maxPax > 0 &&
      !experience.allowedPaxTypes.includes(PAX_TYPE.CHILD)
  );

  const groupedByMaxPax = (hotelExperiences?.experiences ?? []).reduce<
    Record<number, string[]>
  >((acc, exp) => {
    if (exp.maxPax === null || exp.maxPax === undefined) {
      return acc;
    }

    const pax = exp.maxPax;

    if (!acc[pax]) {
      acc[pax] = [];
    }

    acc[pax].push(
      computeExperienceName({
        category: experienceCategories?.find(
          (cat) => cat.id === exp?.categoryId
        ),
        quantity: null,
        brand: exp?.brand ?? null,
        duration: exp?.duration ?? null,
        durationDescription: exp?.durationDescription ?? null,
        time: exp?.time ?? null,
        locations: exp?.location ?? null,
      })
    );

    return acc;
  }, {});

  const renderTooltip = () => {
    if (!groupedByMaxPax || Object.keys(groupedByMaxPax).length === 0) {
      return null;
    }

    return (
      <Tooltip
        title={
          <ul className="tooltip-ul">
            {Object.entries(groupedByMaxPax)
              .sort(([a], [b]) => Number(a) - Number(b))
              .map(([pax, experiences]) => (
                <li key={pax} className="tooltip-li">
                  {pluralize(Number(pax), 'guest', 'guests')} :{' '}
                  {experiences.join(', ')}
                </li>
              ))}
          </ul>
        }
      >
        <InfoCircleOutlined />
      </Tooltip>
    );
  };

  return (
    <>
      <SCard
        title="Guests"
        subtitle="Define who the guests are and how many of them can book this package each day"
        className="pack-form"
      >
        <div>
          <span className="section-title">Minimum guests</span>
          <FormLineLayout
            label="Min. charged pax"
            description="For both adults and children"
            hideDescriptionIcon
          >
            <Form.Item name="minChargedPax">
              {isDisabledBarBbHotel && isEditMode ? (
                <Tooltip title="PAX feature not available for hotels with BAR BB.">
                  <div>
                    <InputNumber
                      value={minChargedPax}
                      min={0}
                      width={90}
                      disabled={true}
                    />
                  </div>
                </Tooltip>
              ) : (
                <InputNumber min={0} width={90} disabled={!isEditMode} />
              )}
            </Form.Item>
          </FormLineLayout>
        </div>
        <div className="maximum-guest">
          <div className="section-title">
            <span>Maximum guests</span>
            {renderTooltip()}
          </div>
          <FormLineLayout
            label="Max. allowed pax"
            description="For both adults and children"
            hideDescriptionIcon
            isRequired={isDaytime}
          >
            <Form.Item
              name="maxAllowedPax"
              validateStatus={
                hasExperienceWithGuests?.maxPax &&
                isEditMode &&
                maxAllowedPax > hasExperienceWithGuests?.maxPax
                  ? 'warning'
                  : undefined
              }
              rules={[
                {
                  required: isDaytime,
                  message: 'Required',
                },
              ]}
              help={
                <>
                  {hasExperienceWithGuests?.maxPax &&
                    isEditMode &&
                    maxAllowedPax > hasExperienceWithGuests?.maxPax && (
                      <div className="warning-message">
                        Please note that at least one experience cannot welcome
                        more than {hasExperienceWithGuests?.maxPax} guests
                      </div>
                    )}
                </>
              }
            >
              {isDisabledBarBbHotel && isEditMode ? (
                <Tooltip title="PAX feature not available for hotels with BAR BB.">
                  <div>
                    <InputNumber
                      value={maxAllowedPax}
                      min={0}
                      width={90}
                      disabled={true}
                    />
                  </div>
                </Tooltip>
              ) : (
                <InputNumber
                  min={0}
                  disabled={!isEditMode}
                  style={{ width: 90 }}
                />
              )}
            </Form.Item>
          </FormLineLayout>
          <FormLineLayout label="Max. allowed adult">
            <Form.Item
              name="maxAllowedAdult"
              dependencies={['maxAllowedPax']}
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    // eslint-disable-next-line @typescript-eslint/no-shadow -- inner-scope reuse, not a bug
                    const maxAllowedPax = getFieldValue('maxAllowedPax');

                    if (
                      value === null ||
                      value === undefined ||
                      maxAllowedPax === null ||
                      maxAllowedPax === undefined ||
                      value <= maxAllowedPax
                    ) {
                      return Promise.resolve();
                    }

                    return Promise.reject(
                      new Error(
                        'Max allowed adults cannot be greater than total max guest'
                      )
                    );
                  },
                }),
              ]}
            >
              <InputNumber min={0} width={90} disabled={!isEditMode} />
            </Form.Item>
          </FormLineLayout>
          <FormLineLayout label="Max. allowed child">
            <Form.Item
              name="maxAllowedChild"
              dependencies={['maxAllowedPax']}
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    // eslint-disable-next-line @typescript-eslint/no-shadow -- inner-scope reuse, not a bug
                    const maxAllowedPax = getFieldValue('maxAllowedPax');

                    if (
                      value === null ||
                      value === undefined ||
                      maxAllowedPax === null ||
                      maxAllowedPax === undefined ||
                      value <= maxAllowedPax
                    ) {
                      return Promise.resolve();
                    }

                    return Promise.reject(
                      new Error(
                        'Max allowed children cannot be greater than total max guest'
                      )
                    );
                  },
                }),
              ]}
              validateStatus={
                hasExperienceWithoutChildren && isEditMode
                  ? 'warning'
                  : undefined
              }
              help={
                hasExperienceWithoutChildren &&
                isEditMode && (
                  <div className="warning-message">
                    Please note that at least one of your experience does not
                    allow children
                  </div>
                )
              }
            >
              <InputNumber min={0} width={90} disabled={!isEditMode} />
            </Form.Item>
          </FormLineLayout>
        </div>
      </SCard>

      <SCard
        title="Bookings"
        subtitle="Define a maximum number of bookings per day for this package"
        className="pack-form"
      >
        <FormLineLayout label="Max. booking" labelOnTop>
          <Form.Item
            initialValue="none"
            name={['maxBookingConfiguration', 'type']}
          >
            <Radio.Group disabled={!isEditMode}>
              <Radio value="none">None</Radio>
              <Radio value="CONSTANT">Same every day</Radio>
              <Radio value="DAILY">Depends on the day</Radio>
            </Radio.Group>
          </Form.Item>
        </FormLineLayout>

        {maxBookingConfiguration?.type === 'CONSTANT' && (
          <FormLineLayout>
            <div className="booking-max-inline">
              <Form.Item
                name={[
                  'maxBookingConfiguration',
                  'configuration',
                  'maxBooking',
                ]}
                style={{ marginBottom: 0 }}
              >
                <InputNumber min={0} width={90} disabled={!isEditMode} />
              </Form.Item>
              <span>booking max. per day</span>
            </div>
          </FormLineLayout>
        )}

        {maxBookingConfiguration?.type === 'DAILY' && (
          <FormLineLayout>
            <Table
              columns={columnsWithInputs}
              dataSource={dataSource}
              pagination={false}
              size="small"
              bordered
            />
          </FormLineLayout>
        )}
      </SCard>
    </>
  );
};
